import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm, 
  Row, 
  Col,
  Tag,
  Upload,
  Divider,
  Statistic,
  Tooltip,
  Drawer,
  Descriptions,
  Grid,
  Dropdown,
  InputNumber,
  Pagination
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UploadOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

interface Consigner {
  id: number;
  subject_type: string[];
  default_station: string;
  name: string;
  address: string;
  phone: string;
  id_card: string;
  goods_name: string;
  unit: string;
  price: number;
  status: '开启' | '关闭';
  remark: string;
}

interface ImportResult {
  success: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; reason: string }>;
}

const ConsignerManagement: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  const [data, setData] = useState<Consigner[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingConsigner, setEditingConsigner] = useState<Consigner | null>(null);
  const [selectedConsigner, setSelectedConsigner] = useState<Consigner | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [requiredFields, setRequiredFields] = useState<string[]>([]); // 不预设必填字段，由用户通过字段映射决定
  const [importData, setImportData] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [searchText, setSearchText] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [shippingStations, setShippingStations] = useState<any[]>([]);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  // 获取收发货站点数据
  const fetchShippingStations = useCallback(async () => {
    try {
      const response = await fetch('/api/shipping-stations?pageSize=1000');
      const result = await response.json();
      if (result.code === 0) {
        setShippingStations(result.data || []);
      }
    } catch (error) {
      console.error('获取收发货站点失败:', error);
    }
  }, []);

  // 获取收发货人列表
  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      // 构建查询参数，包含分页信息
      const queryParams: Record<string, string> = {
        current: current.toString(),
        pageSize: pageSize.toString(),
        ...params
      };
      
      const response = await fetch(`/api/consigners?${new URLSearchParams(queryParams)}`);
      const result = await response.json();
      if (result.code === 0) {
        setData(result.data);
        setTotal(result.total);
      } else {
        messageApi.error({
          content: '获取收发货人数据失败，请刷新页面重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，无法获取收发货人数据，请检查网络连接',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, messageApi]);

  useEffect(() => {
    fetchData();
    fetchShippingStations();
  }, [fetchData, fetchShippingStations]);

  // 搜索
  const handleSearch = useCallback((searchValue?: string) => {
    const searchTextToUse = searchValue !== undefined ? searchValue : searchText;
    if (searchTextToUse.trim()) {
      const params = { search: searchTextToUse.trim() };
      setCurrent(1);
      fetchData(params);
    } else {
      // 如果搜索文本为空，则获取所有数据
      setCurrent(1);
      fetchData();
    }
  }, [fetchData, setCurrent, searchText]);

  // 重置搜索
  const handleReset = useCallback(() => {
    searchForm.resetFields();
    setSearchText('');
    setCurrent(1);
    fetchData();
  }, [fetchData, searchForm, setCurrent]);

  // 分页变化处理
  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrent(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  // 提交表单
  const handleSubmit = useCallback(async (values: any) => {
    try {
      if (editingConsigner) {
        // 更新
        const response = await fetch(`/api/consigners/${editingConsigner.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        if (result.code === 0) {
          messageApi.success({
            content: '收发货人信息更新成功！',
            duration: 3,
          });
          setModalVisible(false);
          fetchData();
        } else {
          messageApi.error({
            content: result.message || '收发货人信息更新失败，请重试',
            duration: 4,
          });
        }
      } else {
        // 创建
        const response = await fetch('/api/consigners', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        if (result.code === 0) {
          messageApi.success({
            content: '收发货人创建成功！',
            duration: 3,
          });
          setModalVisible(false);
          fetchData();
        } else {
          messageApi.error({
            content: result.message || '收发货人创建失败，请重试',
            duration: 4,
          });
        }
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，操作失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [editingConsigner, fetchData, messageApi]);

  // 删除
  const handleDelete = useCallback(async (id: number) => {
    try {
      const response = await fetch(`/api/consigners/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.code === 0) {
        messageApi.success({
          content: '收发货人删除成功！',
          duration: 3,
        });
        fetchData();
      } else {
        messageApi.error({
          content: result.message || '收发货人删除失败，请重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，删除失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [fetchData, messageApi]);

  // 编辑
  const handleEdit = useCallback((record: Consigner) => {
    setEditingConsigner(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  }, [form, setModalVisible]);

  // 新增
  const handleAdd = useCallback(() => {
    setEditingConsigner(null);
    form.resetFields();
    setModalVisible(true);
  }, [form, setModalVisible]);

  // 查看收发货人详情
  const handleView = useCallback((consigner: Consigner) => {
    setSelectedConsigner(consigner);
    setDrawerVisible(true);
  }, [setSelectedConsigner, setDrawerVisible]);

  // 文件上传处理
  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const data = lines.slice(1).filter(line => line.trim()).map((line, index) => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((header, i) => {
            row[header] = values[i] || '';
          });
          row._rowIndex = index + 1;
          return row;
        });

        // 根据字段名称智能推断映射关系，防止重复映射
        const defaultMapping: Record<string, string> = {};
        const usedTargetFields = new Set<string>(); // 防止重复映射
        
        headers.forEach(header => {
          const headerLower = header.toLowerCase();
          let targetField = '';
          
          // 主体类型相关 - 高优先级，非常明确的字段
          if (headerLower.includes('主体类型') || headerLower.includes('subject') || 
              (headerLower.includes('类型') && (headerLower.includes('主体') || headerLower.includes('发货人') || headerLower.includes('收货人')))) {
            targetField = 'subject_type';
          }
          // 姓名相关 - 高优先级，非常明确的字段
          else if (headerLower.includes('姓名') || headerLower.includes('name') || 
                   (headerLower.includes('名字') && !headerLower.includes('货物')) ||
                   (headerLower.includes('联系人') && !headerLower.includes('地址'))) {
            targetField = 'name';
          }
          // 默认卸货站相关 - 高优先级，非常明确的字段
          else if (headerLower.includes('默认卸货站') || headerLower.includes('default_station') || 
                   (headerLower.includes('卸货站') && !headerLower.includes('地址')) ||
                   (headerLower.includes('收货站点') && !headerLower.includes('地址'))) {
            targetField = 'default_station';
          }
          // 地址相关 - 高优先级，非常明确的字段
          else if (headerLower.includes('地址') || headerLower.includes('address') || 
                   (headerLower.includes('location') && !headerLower.includes('电话')) ||
                   (headerLower.includes('详细地址') && !headerLower.includes('联系'))) {
            targetField = 'address';
          }
          // 电话相关 - 高优先级，非常明确的字段
          else if (headerLower.includes('电话') || headerLower.includes('phone') || 
                   (headerLower.includes('手机') && !headerLower.includes('地址')) ||
                   (headerLower.includes('联系电话') && !headerLower.includes('地址'))) {
            targetField = 'phone';
          }
          // 身份证相关 - 高优先级，非常明确的字段
          else if (headerLower.includes('身份证') || headerLower.includes('id_card') || 
                   (headerLower.includes('证件号') && !headerLower.includes('电话'))) {
            targetField = 'id_card';
          }
          // 货物名称相关 - 中等优先级
          else if (headerLower.includes('货物名称') || headerLower.includes('goods_name') ||
                   (headerLower.includes('货物') && !headerLower.includes('地址') && !headerLower.includes('站点')) ||
                   (headerLower.includes('商品名称') && !headerLower.includes('地址'))) {
            targetField = 'goods_name';
          }
          // 单位相关 - 中等优先级
          else if (headerLower.includes('计量单位') || headerLower.includes('unit') ||
                   (headerLower.includes('单位') && !headerLower.includes('地址') && !headerLower.includes('电话'))) {
            targetField = 'unit';
          }
          // 价格相关 - 中等优先级
          else if (headerLower.includes('价格') || headerLower.includes('price') || 
                   (headerLower.includes('单价') && !headerLower.includes('地址'))) {
            targetField = 'price';
          }
          // 状态相关 - 中等优先级
          else if (headerLower.includes('状态') || headerLower.includes('status') ||
                   (headerLower.includes('启用状态') && !headerLower.includes('地址'))) {
            targetField = 'status';
          }
          // 备注相关 - 低优先级，最后考虑
          else if (headerLower.includes('备注') || headerLower.includes('remark') ||
                   (headerLower.includes('说明') && !headerLower.includes('地址') && !headerLower.includes('电话'))) {
            targetField = 'remark';
          }
          
          // 如果目标字段已被使用，则不映射（防止重复）
          if (targetField && usedTargetFields.has(targetField)) {
            targetField = '';
          }
          
          // 设置映射并记录已使用的目标字段
          if (targetField) {
            defaultMapping[header] = targetField;
            usedTargetFields.add(targetField);
          } else {
            defaultMapping[header] = ''; // 让用户手动选择
          }
        });

        setFieldMapping(defaultMapping);
        setImportData(data);
        setImportModalVisible(true);
      } catch (error) {
        messageApi.error({
          content: '文件解析失败，请检查文件格式',
          duration: 4,
        });
      }
    };
    reader.readAsText(file);
    return false; // 阻止自动上传
  }, [messageApi]);

  // 导出CSV文件
  const exportToCSV = useCallback(async () => {
    setExportLoading(true);
    try {
      // 获取所有数据用于导出
      const response = await fetch('/api/consigners?current=1&pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      // 准备导出数据
      const exportData = result.data.map((consigner: Consigner) => ({
        '主体类型': consigner.subject_type.join(', '),
        '姓名': consigner.name,
        '默认卸货站': consigner.default_station,
        '地址': consigner.address,
        '电话': consigner.phone,
        '身份证号': consigner.id_card,
        '货物名称': consigner.goods_name,
        '单位': consigner.unit,
        '价格': consigner.price,
        '状态': consigner.status,
        '备注': consigner.remark
      }));

      // 创建CSV内容
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map((row: Record<string, any>) => 
          headers.map(header => `"${row[header] || ''}"`).join(',')
        )
      ].join('\n');

      // 创建下载链接
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `收发货人_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      messageApi.success({
        content: 'CSV文件导出成功！',
        duration: 3,
      });
    } catch (error) {
      messageApi.error({
        content: 'CSV文件导出失败，请重试',
        duration: 4,
      });
    } finally {
      setExportLoading(false);
    }
  }, [messageApi]);

  // 导出Excel文件
  const exportToExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      // 获取所有数据用于导出
      const response = await fetch('/api/consigners?current=1&pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      // 准备导出数据
      const exportData = result.data.map((consigner: Consigner) => ({
        '主体类型': consigner.subject_type.join(', '),
        '姓名': consigner.name,
        '默认卸货站': consigner.default_station,
        '地址': consigner.address,
        '电话': consigner.phone,
        '身份证号': consigner.id_card,
        '货物名称': consigner.goods_name,
        '单位': consigner.unit,
        '价格': consigner.price,
        '状态': consigner.status,
        '备注': consigner.remark
      }));

      // 创建工作簿和工作表
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // 设置列宽
      const colWidths = [
        { wch: 15 }, // 主体类型
        { wch: 10 }, // 姓名
        { wch: 15 }, // 默认卸货站
        { wch: 30 }, // 地址
        { wch: 15 }, // 电话
        { wch: 20 }, // 身份证号
        { wch: 15 }, // 货物名称
        { wch: 10 }, // 单位
        { wch: 12 }, // 价格
        { wch: 8 },  // 状态
        { wch: 20 }  // 备注
      ];
      worksheet['!cols'] = colWidths;

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(workbook, worksheet, '收发货人');

      // 导出文件
      XLSX.writeFile(workbook, `收发货人_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      messageApi.success({
        content: 'Excel文件导出成功！',
        duration: 3,
      });
    } catch (error) {
      messageApi.error({
        content: 'Excel文件导出失败，请重试',
        duration: 4,
      });
          } finally {
        setExportLoading(false);
      }
    }, [messageApi]);

  // 执行导入
  const handleImport = useCallback(async () => {
    console.log('开始执行导入...'); // 调试日志
    console.log('字段映射:', fieldMapping); // 调试日志
    console.log('导入数据:', importData); // 调试日志
    
    try {
      // 直接按照用户的字段映射处理数据
      const mappedData = importData.map((row, index) => {
        const mapped: any = {};
        Object.keys(fieldMapping).forEach(sourceField => {
          const targetField = fieldMapping[sourceField];
          if (targetField && targetField !== 'skip') {
            mapped[targetField] = row[sourceField];
          }
        });
        mapped._rowIndex = index + 1; // 添加行号用于错误定位
        return mapped;
      });

      console.log('映射后的数据:', mappedData); // 调试日志

      if (mappedData.length === 0) {
        messageApi.error({
          content: '没有数据可以导入，请检查文件内容',
          duration: 4,
        });
        return;
      }

      // 获取用于查重的字段列表（排除"不导入"的字段）
      const checkFields = Object.values(fieldMapping).filter(field => field !== 'skip');
      console.log('查重字段:', checkFields); // 调试日志

      console.log('准备发送到后端，数据:', mappedData); // 调试日志

      // 发送到后端导入，传递查重字段信息
      const response = await fetch('/api/consigners/batch-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: mappedData,
          checkFields: checkFields // 传递查重字段列表
        }),
      });

      console.log('后端响应状态:', response.status); // 调试日志

      const result = await response.json();
      
      console.log('后端返回结果:', result); // 调试用
      
      if (result.success) {
        // 显示详细的导入结果
        const successCount = result.successCount || 0;
        const skipCount = result.skipCount || 0;
        const errorCount = result.errorCount || 0;
        
        let resultMessage = `导入完成！成功: ${successCount} 条`;
        if (skipCount > 0) {
          resultMessage += `，跳过: ${skipCount} 条`;
        }
        if (errorCount > 0) {
          resultMessage += `，失败: ${errorCount} 条`;
        }
        
        messageApi.success({
          content: resultMessage,
          duration: 4,
        });
        
        // 如果有失败记录，显示失败详情
        if (errorCount > 0 && result.errors && result.errors.length > 0) {
          Modal.info({
            title: '导入失败详情',
            width: 600,
            content: (
              <div>
                <p>以下记录导入失败：</p>
                <ul style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {result.errors.map((error: any, index: number) => (
                    <li key={index} style={{ marginBottom: '8px' }}>
                      <strong>第 {error.rowIndex || error.row || (index + 1)} 行:</strong> {error.reason || error.message}
                    </li>
                  ))}
                </ul>
              </div>
            ),
          });
        }
        
        // 构建导入结果 - 完全使用后端返回的数据
        const importResult: ImportResult = {
          success: successCount,
          skipped: skipCount,
          failed: errorCount,
          errors: result.errors ? result.errors.map((err: any) => ({
            row: err.rowIndex || err.row || 0,
            reason: err.reason || err.message
          })) : []
        };

        setImportResult(importResult);
        
        // 强制刷新数据列表
        setTimeout(() => {
          fetchData();
        }, 500);
        
        // 不清空数据，让用户自己决定何时关闭
        // setImportModalVisible(false);
        // setImportData([]);
        // setFieldMapping({});
      } else {
        messageApi.error({
          content: result.message || '导入失败，请检查数据格式',
          duration: 4,
        });
      }
    } catch (error) {
      console.error('导入过程中发生错误:', error); // 调试日志
      messageApi.error({
        content: '导入过程中发生错误，请重试',
        duration: 4,
      });
    }
  }, [fieldMapping, importData, fetchData, messageApi]);



  // 渲染收发货人卡片（移动端）
  const renderConsignerCard = useCallback((consigner: Consigner) => (
    <Card 
      key={consigner.id} 
      size="small" 
      style={{ marginBottom: 12 }}
      actions={[
        <Tooltip title="查看详情" key="view">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleView(consigner)}
          >
            详情
          </Button>
        </Tooltip>,
        <Tooltip title="编辑" key="edit">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEdit(consigner)}
          >
            编辑
          </Button>
        </Tooltip>,
        <Popconfirm
          title="确定要删除这个收发货人吗？"
          onConfirm={() => handleDelete(consigner.id)}
          okText="确定"
          cancelText="取消"
          key="delete"
        >
          <Tooltip title="删除">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Tooltip>
        </Popconfirm>
      ]}
    >
      <Row gutter={[8, 6]}>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">姓名：</Text>
            <div style={{ textAlign: 'right' }}>
              <Text strong style={{ fontSize: 16 }}>{consigner.name}</Text>
              <Tag color={consigner.status === '开启' ? 'green' : 'red'} style={{ marginLeft: 6 }}>
                {consigner.status}
              </Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">主体类型：</Text>
            <div style={{ textAlign: 'right' }}>
              {consigner.subject_type.map((type, index) => (
                <Tag key={index} color={type === '发货人' ? 'blue' : 'green'} style={{ marginLeft: 4 }}>
                  {type}
                </Tag>
              ))}
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">默认卸货站：</Text>
            <Text style={{ textAlign: 'right' }}>{consigner.default_station || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">地址：</Text>
            <Text style={{ textAlign: 'right' }}>{consigner.address || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">电话：</Text>
            <Text copyable style={{ textAlign: 'right' }}>{consigner.phone || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">货物名称：</Text>
            <Text style={{ textAlign: 'right' }}>{consigner.goods_name || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">价格：</Text>
            <Text style={{ textAlign: 'right' }}>{consigner.price ? `¥${consigner.price}` : '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Text type="secondary">备注：</Text>
            <div style={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{consigner.remark || '无'}</Text>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  ), [handleView, handleEdit, handleDelete]);

  // 表格列定义
  const columns = useMemo(() => [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
      fixed: 'left' as const,
      width: isMobile ? 100 : 120
    },
    {
      title: '主体类型',
      dataIndex: 'subject_type',
      key: 'subject_type',
      render: (types: string[]) => (
        <div>
          {types.map((type, index) => (
            <Tag key={index} color={type === '发货人' ? 'blue' : 'green'}>
              {type}
            </Tag>
          ))}
        </div>
      ),
      width: isMobile ? 120 : 150
    },
    {
      title: '默认卸货站',
      dataIndex: 'default_station',
      key: 'default_station',
      render: (station: string) => station || '-',
      width: isMobile ? 120 : 150
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      render: (address: string) => address || '-',
      width: isMobile ? 120 : 200,
      ellipsis: true
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
      width: isMobile ? 100 : 120
    },
    {
      title: '身份证号',
      dataIndex: 'id_card',
      key: 'id_card',
      render: (idCard: string) => idCard || '-',
      width: isMobile ? 120 : 150,
      responsive: ['lg'] as any
    },
    {
      title: '货物名称',
      dataIndex: 'goods_name',
      key: 'goods_name',
      render: (goods: string) => goods || '-',
      width: isMobile ? 100 : 120,
      responsive: ['lg'] as any
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => price ? `¥${price}` : '-',
      width: isMobile ? 80 : 100,
      responsive: ['lg'] as any
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === '开启' ? 'green' : 'red'}>
          {status}
        </Tag>
      ),
      width: isMobile ? 60 : 80
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: isMobile ? 120 : 150,
      render: (_: any, record: Consigner) => (
        <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
          <Tooltip title="查看详情">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个收发货人吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                size={isMobile ? 'small' : 'middle'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ], [isMobile, handleView, handleEdit, handleDelete]);

  return (
    <>
      {contextHolder}
      <div style={{ padding: isMobile ? '16px' : '12px' }}>
        <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={8}>
              <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>收发货人管理</Title>
            </Col>
            <Col xs={24} sm={24} md={16}>
              <Space 
                direction={isMobile ? 'vertical' : 'horizontal'} 
                style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}
                size="small"
              >
                <Input
                  placeholder="搜索姓名、电话或地址"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchText(value);
                    // 输入即搜索，延迟300ms避免频繁请求
                    if (searchTimeout.current) {
                      clearTimeout(searchTimeout.current);
                    }
                    searchTimeout.current = setTimeout(() => {
                      handleSearch(value);
                    }, 300);
                  }}
                  allowClear
                  style={{ width: isMobile ? '100%' : 250 }}
                />
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchData}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  刷新
                </Button>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => setImportModalVisible(true)}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  批量导入
                </Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'csv',
                        label: '导出CSV',
                        onClick: exportToCSV,
                        icon: <DownloadOutlined />
                      },
                      {
                        key: 'excel',
                        label: '导出Excel',
                        onClick: exportToExcel,
                        icon: <DownloadOutlined />
                      }
                    ]
                  }}
                >
                  <Button 
                    icon={<DownloadOutlined />} 
                    loading={exportLoading}
                    style={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    导出
                  </Button>
                </Dropdown>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAdd}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  新增收发货人
                </Button>
              </Space>
            </Col>
          </Row>
          {isMobile && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                共 {total} 个收发货人（当前页显示 {data.length} 个）
              </Text>
            </div>
          )}
        </div>

        {isMobile ? (
          // 移动端卡片布局
          <div>
            {data.map(renderConsignerCard)}
            {data.length === 0 && !loading && (
              <Card style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">暂无收发货人数据</Text>
              </Card>
            )}
            
            {/* 移动端分页 */}
            {total > pageSize && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Pagination
                  current={current}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger={false}
                  showQuickJumper={true}
                  showTotal={(total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                  onChange={handlePageChange}
                  size="small"
                />
              </div>
            )}
          </div>
        ) : (
          // 桌面端表格布局
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={{
              current: current,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange
            }}
            size="middle"
          />
        )}
      </Card>
    </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingConsigner ? '编辑收发货人' : '新增收发货人'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '98%' : 800}
        style={{ top: isMobile ? 20 : 100 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="subject_type"
            label="主体类型"
            rules={[{ required: true, message: '请选择主体类型' }]}
          >
            <Select
              mode="multiple"
              placeholder="请选择主体类型"
              options={[
                { label: '发货人', value: '发货人' },
                { label: '收货人', value: '收货人' }
              ]}
            />
          </Form.Item>
          
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          
          <Form.Item
            name="default_station"
            label="默认卸货站"
          >
            <Select
              placeholder="请选择默认卸货站"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={shippingStations.map(station => ({
                label: station.name,
                value: station.name
              }))}
            />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="开启">开启</Option>
              <Option value="关闭">关闭</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="address"
            label="地址"
          >
            <Input placeholder="请输入地址" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="电话"
          >
            <Input placeholder="请输入电话" />
          </Form.Item>
          
          <Form.Item
            name="id_card"
            label="身份证号"
          >
            <Input placeholder="请输入身份证号" />
          </Form.Item>

          <Form.Item
            name="goods_name"
            label="货物名称"
          >
            <Input placeholder="请输入货物名称" />
          </Form.Item>
          
          <Form.Item
            name="unit"
            label="单位"
          >
            <Input placeholder="请输入单位" />
          </Form.Item>
          
          <Form.Item
            name="price"
            label="价格"
          >
            <InputNumber
              placeholder="请输入价格"
              style={{ width: '100%' }}
              min={0}
              precision={2}
              addonAfter="元"
            />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea 
              placeholder="请输入备注" 
              rows={3}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingConsigner ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入收发货人"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <Upload
            beforeUpload={handleFileUpload}
            accept=".csv"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>选择CSV文件</Button>
          </Upload>
          <span style={{ marginLeft: '8px', color: '#666' }}>
            支持CSV格式，第一行为表头
          </span>
        </div>

        {importData.length > 0 && (
          <>
            <Divider>字段映射</Divider>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '12px' }}>请将源文件字段映射到目标字段</h4>
              
              {/* 字段映射说明 */}
              <div style={{ 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f0f8ff',
                border: '1px solid #91d5ff',
                borderRadius: '4px'
              }}>
                <Text strong style={{ color: '#1890ff' }}>字段映射说明：</Text>
                <Text style={{ marginLeft: '8px' }}>
                  请将源文件字段映射到对应的目标字段，选择"不导入"可跳过不需要的字段
                </Text>
              </div>
              
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {Object.keys(fieldMapping).map((field, index) => {
                  const targetField = fieldMapping[field];
                  
                  return (
                    <div key={field} style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      {/* 源文件字段 */}
                      <div style={{ 
                        flex: '1',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{ fontWeight: '500', color: '#1890ff' }}>{field}</span>
                      </div>
                      
                      {/* 映射箭头 */}
                      <div style={{ 
                        fontSize: '18px',
                        color: '#1890ff',
                        fontWeight: 'bold',
                        padding: '0 8px'
                      }}>
                        →
                      </div>
                      
                      {/* 目标字段 */}
                      <div style={{ flex: '1' }}>
                        <Select
                          value={fieldMapping[field]}
                          onChange={(value) => setFieldMapping({ ...fieldMapping, [field]: value })}
                          style={{ width: '100%' }}
                          placeholder="请选择对应字段"
                        >
                          <Option value="subject_type">主体类型</Option>
                          <Option value="name">姓名</Option>
                          <Option value="default_station">默认卸货站</Option>
                          <Option value="address">地址</Option>
                          <Option value="phone">电话</Option>
                          <Option value="id_card">身份证号</Option>
                          <Option value="goods_name">货物名称</Option>
                          <Option value="unit">单位</Option>
                          <Option value="price">价格</Option>
                          <Option value="status">状态</Option>
                          <Option value="remark">备注</Option>
                          <Option value="skip">不导入</Option>
                        </Select>
                      </div>
                      
                      {/* 字段状态标识 */}
                      <div style={{ 
                        width: '60px',
                        textAlign: 'center'
                      }}>
                        {targetField && (
                          <Tag color={
                            targetField === 'skip' ? 'default' : 'green'
                          }>
                            {targetField === 'skip' ? '不导入' : '导入'}
                          </Tag>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Divider>数据预览（前5条）</Divider>
            <Table
              dataSource={importData.slice(0, 5)}
              columns={Object.keys(fieldMapping).map(field => ({
                title: field,
                dataIndex: field,
                key: field,
              }))}
              pagination={false}
              size="small"
            />

            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setImportModalVisible(false)}>
                  取消
                </Button>
                <Button type="primary" onClick={handleImport}>
                  开始导入
                </Button>
              </Space>
            </div>
          </>
        )}

        {/* 导入结果 */}
        {importResult && (
          <>
            <Divider>导入结果</Divider>
            
            {/* 结果统计 */}
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={8}>
                <Statistic 
                  title="成功导入" 
                  value={importResult.success} 
                  valueStyle={{ color: '#3f8600' }}
                  suffix="条记录"
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="跳过记录" 
                  value={importResult.skipped} 
                  valueStyle={{ color: '#faad14' }}
                  suffix="条记录"
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="失败记录" 
                  value={importResult.failed} 
                  valueStyle={{ color: '#cf1322' }}
                  suffix="条记录"
                />
              </Col>
            </Row>
            
            {/* 错误详情 */}
            {importResult.errors.length > 0 && (
              <div style={{ 
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '4px'
              }}>
                <h4 style={{ color: '#cf1322', marginBottom: '12px' }}>详细说明：</h4>
                
                {/* 跳过记录说明 */}
                {importResult.errors.filter(error => error.reason.includes('重复记录')).length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#faad14' }}>跳过记录原因：</Text>
                    <Text style={{ marginLeft: '8px' }}>
                      系统检测到重复记录（姓名和电话组合已存在），已自动跳过
                    </Text>
                  </div>
                )}
                
                {/* 失败记录说明 */}
                {importResult.errors.filter(error => error.reason.includes('必填字段')).length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#cf1322' }}>失败记录原因：</Text>
                    <Text style={{ marginLeft: '8px' }}>
                      必填字段数据不完整，无法导入
                    </Text>
                  </div>
                )}
                
                {/* 具体错误列表 */}
                <div style={{ marginTop: '12px' }}>
                  <Text strong>具体错误：</Text>
                  {importResult.errors.map((error, index) => (
                    <div key={index} style={{ 
                      color: '#cf1322', 
                      marginBottom: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#fff',
                      borderRadius: '2px'
                    }}>
                      <Text strong>第{error.row}行：</Text> {error.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* 导入完成后的操作按钮 */}
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Space size="large">
                <Button 
                  type="primary" 
                  onClick={() => {
                    setImportModalVisible(false);
                    setImportData([]);
                    setFieldMapping({});
                    setImportResult(null);
                  }}
                >
                  完成并关闭
                </Button>
                <Button 
                  onClick={() => {
                    setImportData([]);
                    setFieldMapping({});
                    setImportResult(null);
                  }}
                >
                  重新导入
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>

      {/* 收发货人详情抽屉 */}
      <Drawer
        title="收发货人详情"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? '100%' : 700}
        height={isMobile ? '85%' : undefined}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedConsigner && (
          <div>
            <Descriptions title="基本信息" bordered column={1}>
              <Descriptions.Item label="姓名">{selectedConsigner.name}</Descriptions.Item>
              <Descriptions.Item label="主体类型">
                {selectedConsigner.subject_type.map((type, index) => (
                  <Tag key={index} color={type === '发货人' ? 'blue' : 'green'} style={{ marginRight: 4 }}>
                    {type}
                  </Tag>
                ))}
              </Descriptions.Item>
              <Descriptions.Item label="默认卸货站">{selectedConsigner.default_station || '-'}</Descriptions.Item>
              <Descriptions.Item label="地址">{selectedConsigner.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="电话">{selectedConsigner.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="身份证号">{selectedConsigner.id_card || '-'}</Descriptions.Item>
              <Descriptions.Item label="货物名称">{selectedConsigner.goods_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="单位">{selectedConsigner.unit || '-'}</Descriptions.Item>
              <Descriptions.Item label="价格">{selectedConsigner.price ? `¥${selectedConsigner.price}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={selectedConsigner.status === '开启' ? 'green' : 'red'}>
                  {selectedConsigner.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="备注">{selectedConsigner.remark || '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default ConsignerManagement;
